/* ===========================================================================
 * EVERY WORD, SOMEWHERE — the one search rule, for the browser and the APK.
 *
 * The server has matched people this way since 2026-09-26
 * (`packages/api/src/utils/nameSearch.ts`, `everyWordIn`): each typed word must
 * appear in SOME field, in any order, so "ali khan" finds Ali Ahmed Khan and
 * "Salah ST097" finds the man with that code. The browser did not. Dispatch,
 * corrections, leave, the team board and every `useClientControls` list
 * compared the WHOLE phrase against one string, so the same query found a
 * person on Employees and nobody on Dispatch — two screens stating different
 * things about one fact (design-rules §3).
 *
 * This is the rule as a predicate, so a list that arrived whole is searched
 * exactly as a paginated one is searched on the server. It is the server's
 * semantics, not a second opinion of them:
 *
 *   - the words are the SAME words: split on whitespace, at most
 *     `SEARCH_MAX_WORDS` of them (`searchWords`, which `everyWordIn` reads too);
 *   - one word is "contained in any field"; several are "each one contained in
 *     some field" — a word may not be split across two fields;
 *   - a missing field (`null` / `undefined`) contains nothing, as a NULL
 *     column matches no `contains` in SQL;
 *   - case and accents do not count, which is what MySQL's `_ci` collation does
 *     to a `contains` (`foldForSearch`).
 *
 * `everyWord.parity.test.ts` in the API holds the two to one fixture table.
 * ======================================================================== */

/** At most this many words are matched; each adds a LIKE group on the server, and nobody's name is longer. */
export const SEARCH_MAX_WORDS = 6;

/** The words a query is matched by — the server's split, exactly. */
export function searchWords(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean).slice(0, SEARCH_MAX_WORDS);
}

/**
 * Case- and accent-blind form of a string, the way a `_ci` collation compares.
 * Combining marks go (é → e, and Arabic harakat), so a name typed without
 * diacritics finds the one stored with them.
 */
export function foldForSearch(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** A word needs at least this many digits before it may match a code by its digits alone. */
export const CODE_DIGITS_MIN = 2;

export interface EveryWordOptions {
  /**
   * CODES BY THEIR DIGITS — opt-in, for a board read off a printed list
   * (Dispatch). A word carrying at least `CODE_DIGITS_MIN` digits also finds a
   * code whose digits contain them, so "102", "W-102" and "w102" all find
   * W-10233 while one digit does not match half the roster. It widens one
   * WORD, never the rule: every word must still match. The server has no such
   * clause, so only a list that arrived whole offers it, and its placeholder
   * says "code".
   */
  codesByDigits?: readonly (string | null | undefined)[];
}

const digitsOf = (s: string): string => s.replace(/\D/g, '');

/**
 * Does `query` find a record whose searchable fields are `fields`?
 *
 * An empty query narrows nothing, so it matches — a caller never has to
 * special-case the empty box.
 */
export function everyWordMatches(
  query: string,
  fields: readonly (string | null | undefined)[],
  options: EveryWordOptions = {}
): boolean {
  const words = searchWords(query).map(foldForSearch);
  if (words.length === 0) return true;
  const haystacks = fields.filter((f): f is string => typeof f === 'string').map(foldForSearch);
  const codeDigits = (options.codesByDigits ?? [])
    .filter((c): c is string => typeof c === 'string')
    .map(digitsOf)
    .filter(Boolean);
  const byDigits = (word: string): boolean => {
    const digits = digitsOf(word);
    return digits.length >= CODE_DIGITS_MIN && codeDigits.some((c) => c.includes(digits));
  };
  return words.every((word) => haystacks.some((h) => h.includes(word)) || byDigits(word));
}

/** Filter a list by the rule — `fieldsOf` names what is searched, and the placeholder must say the same. */
export function filterByEveryWord<T>(
  rows: readonly T[],
  query: string,
  fieldsOf: (row: T) => readonly (string | null | undefined)[]
): T[] {
  if (searchWords(query).length === 0) return [...rows];
  return rows.filter((row) => everyWordMatches(query, fieldsOf(row)));
}
